import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { logAnalyticsEvent } from '../utils/firebase';

const StatisticsSection = styled.section`
  padding: 5rem 0;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const ImageContainer = styled.div`
  position: relative;
  height: 100%;
  border-radius: 12px;
  overflow: hidden;
  transform: translateY(50px);
  opacity: 0;
  transition: all 1s ease;
  
  &.visible {
    transform: translateY(0);
    opacity: 1;
  }
`;

const MainImage = styled.img`
  width: 100%;
  height: auto;
  border-radius: 12px;
`;

const StatsOverlay = styled.div`
  position: absolute;
  border-radius: 17px;
  width: 94%;
  justify-self: center;
  bottom: 6rem;
  left: 0;
  right: 0;
  background: linear-gradient(135deg, ${props => props.theme.colors.primary}, #800000);
  padding: 2rem;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  color: white;
  text-align: center;
  transform: translateY(100%);
  transition: transform 0.8s ease;
  
  &.visible {
    transform: translateY(0);
  }
  
  @media (max-width: 768px) {
    bottom: 0rem;
  }
`;

const StatItem = styled.div`
  border-right: 1px solid rgba(255, 255, 255, 0.2);
  padding: 0 1rem;
  
  &:last-child {
    border-right: none;
  }
  
  @media (max-width: 480px) {
    padding: 0 0.5rem;
  }
`;



const StatNumber = styled.div`
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  opacity: 0;
  transform: translateY(20px);
  transition: all 0.5s ease;
  transition-delay: ${props => props.delay || '0s'};
  
  &.visible {
    opacity: 1;
    transform: translateY(0);
  }
  
  @media (max-width: 480px) {
    font-size: 1.5rem;
  }
`;

const StatLabel = styled.div`
  font-size: 0.9rem;
  opacity: 0.9;
  line-height: 1.2;
`;

const ContentContainer = styled.div`
  opacity: 0;
  transform: translateX(50px);
  transition: all 1s ease;
  
  &.visible {
    opacity: 1;
    transform: translateX(0);
  }
  
  @media (max-width: 768px) {
    text-align: center;
  }
`;

const Category = styled.h4`
  color: ${props => props.theme.colors.primary};
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 1rem;
  text-transform: uppercase;
`;

const Title = styled.h2`
  font-size: 2.5rem;
  color: ${props => props.theme.colors.dark};;
  margin-bottom: 1.5rem;
  font-weight: 700;
  line-height: 1.2;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const Description = styled.p`
  color: #666;
  line-height: 1.6;
  margin-bottom: 2rem;
`;

const FeaturesList = styled.div`
  display: inline-flex;
  width: 53%;
  flex-direction: column;
  gap: 1rem;
  @media (max-width: 768px) {
    justify-self: center;
    width: 73%;
  }
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: rgb(102, 102, 102);
  svg {
    color: ${props => props.theme.colors.primary};
  }
  
  @media (max-width: 768px) {
    justify-content: center;
  }
`;
const DivDisplay = styled.div`
  display: flex;
  alignItems: center;
 @media (max-width: 768px) {
    display: grid;
  }
`;
const ExperienceBadge = styled.div`
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: ${props => props.theme.colors.primary};
  color: white;
  padding: 1rem;
  margin: 2rem 0;
  border-radius: 25px;
  min-width: 10rem;
  
  span:first-child {
    font-size: 2rem;
    font-weight: 400;
  }
  
  span:last-child {
    font-size: 0.8rem;
    text-transform: uppercase;
    text-align: center;
  }
    
`;

const LearnMoreButton = styled.button`
  background: ${props => props.theme.colors.primary};
  color: white;
  border: none;
  padding: 0.8rem 2rem;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  margin-top: 1rem;
  transition: background 0.3s ease;
  
  &:hover {
    background: #A01830;
  }
`;

function AboutUs() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = React.useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      { threshold: 0.3 }
    );

    const currentRef = sectionRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);
  return (
    <StatisticsSection ref={sectionRef}>
      <Container>
        <ImageContainer className={isVisible ? 'visible' : ''}>
          <MainImage src="/images/sliders/fam-image-opt.webp" alt="Our Team" width="800" height="534" loading="lazy" />
          <StatsOverlay className={isVisible ? 'visible' : ''}>
            <StatItem>
              <StatNumber className={isVisible ? 'visible' : ''} delay="0.2s">20+</StatNumber>
              <StatLabel>Actions completed</StatLabel>
            </StatItem>
            <StatItem>
              <StatNumber className={isVisible ? 'visible' : ''} delay="0.4s">70+</StatNumber>
              <StatLabel>Members</StatLabel>
            </StatItem>
            <StatItem>
              <StatNumber className={isVisible ? 'visible' : ''} delay="0.6s">24+</StatNumber>
              <StatLabel>Target schools to renovate</StatLabel>
            </StatItem>
          </StatsOverlay>
        </ImageContainer>
        
        <ContentContainer className={isVisible ? 'visible' : ''}>
          <Category>ABOUT US</Category>
          <Title>Who We Are & Why We Care</Title>
          <Description>
            Driven by compassion, powered by community, The Big Heart Association is on a mission to bring sustainable change to those who need it most.
          </Description>
          <DivDisplay> 
          <FeaturesList>
           <FeatureItem>
              <FontAwesomeIcon icon={faCheck} style={{ color: '#D8AC56' }} />
              Empowering Communities
            </FeatureItem>
            <FeatureItem>
              <FontAwesomeIcon icon={faCheck} style={{ color: '#D8AC56' }} />
              Transforming Lives
            </FeatureItem>
            <FeatureItem>
              <FontAwesomeIcon icon={faCheck} style={{ color: '#D8AC56' }} />
              United by Compassion
            </FeatureItem>
            <FeatureItem>
              <FontAwesomeIcon icon={faCheck} style={{ color: '#D8AC56' }} />
              Building Brighter Futures
            </FeatureItem>
            
          </FeaturesList>
          <ExperienceBadge>
            <span>3+</span>
            <span>YEARS</span>
            <span>OF EXPERIENCE</span>
          </ExperienceBadge></DivDisplay>
          <LearnMoreButton onClick={() => window.location.href = '/about'}>Learn More</LearnMoreButton>
        </ContentContainer>
      </Container>
    </StatisticsSection>
  );
}

export default AboutUs;
